import type { ReactNode } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  AppstoreOutlined, FundOutlined, InboxOutlined, LogoutOutlined, MoonOutlined,
  PieChartOutlined, ShopOutlined, SunOutlined, UserOutlined,
} from '@ant-design/icons'
import { useAuth } from '@argo/common/auth'
import { useTheme } from '@argo/common/theme'
import { config } from '@argo/common/config'
import LanguageMenu from '@argo/common/components/LanguageMenu'
import { isStaff, useSession } from '../session'

/**
 * A customer sees three tabs — [Balance] [Products] [Profile], as the product
 * plan lays them out; claims open from the balance. The insurer's staff see
 * their desks as well. `short` is what the bottom bar uses, where six labels
 * share a phone width.
 */
const customerTabs = [
  { to: '/', key: 'tabBalance', icon: <PieChartOutlined /> },
  { to: '/products', key: 'catalog', icon: <AppstoreOutlined /> },
]
// A desk opens for any of its roles: the products page also carries the
// issuer's panel for putting settlement money on the ledger, and an issuer is
// not necessarily an underwriter.
const staffTabs = [
  { to: '/insurer/claims', key: 'queue', short: 'shortQueue', icon: <InboxOutlined />, roles: ['claims'] },
  { to: '/insurer/products', key: 'productsDesk', short: 'shortDesk', icon: <ShopOutlined />, roles: ['underwriter', 'issuer'] },
  { to: '/insurer/report', key: 'report', icon: <FundOutlined />, roles: ['actuary'] },
] as const

export default function Layout({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const { me } = useSession()
  const { resolved, setMode } = useTheme()
  const { signOut } = useAuth()
  const location = useLocation()

  const staff = isStaff(me)
  const desks = staffTabs.filter(tab =>
    (staff || me?.staff?.issuer) &&
    (tab.roles.some(r => me?.staff?.[r]) || me?.roles.includes('operator')))
  const tabs: { to: string; key: string; short?: string; icon: React.ReactNode }[] =
    [...customerTabs, ...desks, { to: '/profile', key: 'profile', icon: <UserOutlined /> }]

  // Keying the view on the route restarts its entrance animation once per
  // navigation, and never when data underneath it refreshes.
  const viewKey = location.pathname.split('/').slice(0, 3).join('/')

  return (
    <div className="app">
      <header className="topbar">
        <div className="brand">
          <span className="brand-mark" aria-hidden>A</span>
          <span className="only-wide">{t('app')}</span>
        </div>
        <nav className="topnav">
          {tabs.map(tab => (
            <NavLink key={tab.to} to={tab.to} end={tab.to === '/'}>{t(tab.key)}</NavLink>
          ))}
        </nav>
        <span className="topbar-spacer" />
        <div className="topbar-actions">
          <LanguageMenu />
          <button
            className="icon-btn"
            onClick={() => setMode(resolved === 'dark' ? 'light' : 'dark')}
            aria-label={t('theme')}
            title={t('theme')}
          >
            {resolved === 'dark' ? <SunOutlined /> : <MoonOutlined />}
          </button>
          {config.auth !== 'off' && (
            <button className="icon-btn" onClick={signOut} aria-label={t('signOut')}
              title={`${t('signOut')}${me ? ` · ${me.username}` : ''}`}>
              <LogoutOutlined />
            </button>
          )}
        </div>
      </header>

      <main className="main">
        <div className="view" key={viewKey}>{children}</div>
      </main>

      <nav className="tabbar">
        <div className="tabbar-inner">
          {tabs.map(tab => (
            <NavLink key={tab.to} to={tab.to} end={tab.to === '/'}>
              {tab.icon}
              <span>{t(tab.short ?? tab.key)}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  )
}
